import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import RefreshIcon from '@material-ui/icons/Refresh';

import { STAGE_COL_WIDTHS, useStyles } from './Styles';
import { COUNT_DASH, formatCount, formatPercent } from './formatters';
import { downloadExecutiveSummaryReport } from './report';
import {
  fetchExecCmoOptionsAPI,
  fetchExecProjectOptionsAPI,
  fetchExecSiteOptionsAPI,
  fetchExecutiveSummaryAPI,
} from './api';

/**
 * Executive Summary (Executive Summary - Functional Specs; BMS PD Enhancements
 * [Set 2] slides 2–8).
 *
 * "How many projects, campaigns and batches are completed or in-progress" —
 * the campaigns matching the chosen CMO / Site / Project ID, split into Ongoing
 * and Past sections, each campaign showing one row per stage with its Planned /
 * Completed / Ongoing batch counts and Completion %.
 *
 * Every count is derived in the middleware from Batch Status (FSD §7); nothing
 * here recomputes them. This file is the screen: filters, the two collapsible
 * sections, and the report download.
 *
 * Like Batch Analytics and the Gantt Chart this is an in-app full-screen view,
 * opened and closed by ProductDevelopment through ProjectDevNavContext, so the
 * browser URL never changes and the dashboard's state survives underneath.
 */

const ALL = 'all';
const EMPTY_RESULTS = { lastSyncedAt: null, tiles: null, ongoing: [], past: [] };

// The summary tiles, in the order and accent colours FSD v3 §5 gives them.
//
// Planned Campaigns was the second of four. Product dropped it on 2026-10-08 —
// Sai does not send the data behind it — and the rule that held such a campaign
// out of both sections went with it, so nothing is hidden by its absence.
const TILES = [
  ['ongoingCampaigns', 'Ongoing Campaigns', '#E8833A'],
  ['completedCampaigns', 'Completed Campaigns', '#2E9E4F'],
  ['openDeviations', 'Open Deviations', '#D5392F'],
];

// A tile count is zero-padded like every other count on this screen, but unlike
// a batch count a real zero is meaningful here - "no campaigns are planned" is
// a fact, not a gap - so 0 prints as 00 and only a missing value reads NA.
const formatTileCount = (value) => {
  const n = Number(value);
  if (value == null || !Number.isFinite(n)) return COUNT_DASH;
  return String(n).padStart(2, '0');
};

// A selection is either ["all"] or a list of ids. The API strips "all", so the
// two spellings of "don't filter" collapse to the same request.
// The endpoints take a list; the filters now hold one value. ALL and "nothing
// chosen" both mean "do not filter on this", so both collapse to an empty list.
const idsFor = (selection) => (!selection || selection === ALL ? [] : [selection]);

/**
 * One of the three filters. FSD v3 §4: "single-select dropdown (only one option
 * can be selected) ... The filters are dropdowns, not checkboxes: selecting a
 * new option replaces the previous one", and "'All' is a valid selection for
 * each filter".
 *
 * These were checkbox lists, which let a user pick three CMOs at once — a state
 * the screen's own wording ("Choose CMO") never described, and which made the
 * collapsed label ambiguous once more than one was ticked.
 */
const SingleSelect = ({ classes, label, placeholder, options, value, onChange }) => {
  const labelById = useMemo(() => {
    const map = new Map();
    options.forEach((o) => map.set(String(o.id), o.label));
    return map;
  }, [options]);

  return (
    <div>
      <div className={classes.fieldLabel}>{label}</div>
      <FormControl variant="outlined" size="small" className={classes.field}>
        <Select
          displayEmpty
          value={value}
          onChange={(event) => onChange(event.target.value)}
          renderValue={(selected) => {
            if (!selected) return <span className={classes.placeholder}>{placeholder}</span>;
            if (selected === ALL) return 'All';
            return labelById.get(String(selected)) || selected;
          }}
          MenuProps={{
            getContentAnchorEl: null,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
          }}
        >
          <MenuItem value={ALL}>All</MenuItem>
          {options.map((o) => (
            <MenuItem key={o.id} value={String(o.id)}>{o.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

/**
 * One of the four summary tiles (FSD v3 §5). The count is zero-padded to two
 * digits like every other count on this screen; a count the server could not
 * produce reads "NA" rather than a fabricated zero.
 */
const SummaryTile = ({ classes, accent, count, label }) => (
  <div className={classes.tile}>
    <span className={classes.tileAccent} style={{ background: accent }} />
    <div className={classes.tileBody}>
      <div className={classes.tileCount} style={{ color: accent }}>{formatTileCount(count)}</div>
      <div className={classes.tileLabel}>{label}</div>
    </div>
  </div>
);

const STAGE_COLUMNS = [
  'Stage',
  'Planned Batches',
  'Completed Batches',
  'Ongoing Batches',
  'Completion (%)',
];

// A cell whose value is a dash carries no number, so it is greyed back rather
// than sitting at the same weight as a real count.
const Count = ({ classes, text, extra = '', last }) => (
  <TableCell
    className={[classes.bodyCell, last ? classes.lastRowCell : '', extra,
      text === COUNT_DASH ? classes.dashCell : ''].filter(Boolean).join(' ')}
  >
    {text}
  </TableCell>
);

const StageTable = ({ classes, stages }) => (
  <div className={classes.tableWrap}>
    <Table size="small" className={classes.table}>
      <colgroup>
        {STAGE_COL_WIDTHS.map((w, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <col key={i} style={{ width: w }} />
        ))}
      </colgroup>
      <TableHead>
        <TableRow>
          {STAGE_COLUMNS.map((label) => (
            <TableCell key={label} className={classes.headCell}>{label}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {stages.map((stage, i) => {
          const last = i === stages.length - 1;
          return (
            <TableRow key={stage.collabrixCampaignStageID}>
              <TableCell
                className={[classes.bodyCell, classes.stageCell, last ? classes.lastRowCell : '']
                  .filter(Boolean).join(' ')}
              >
                {/* FSD v3 §6: "Stage name with the synthetic step name beneath
                    it (e.g. 'Stage A' / 'Synthetic Step A')". The step letter is
                    the stage's own CMO id; without one there is nothing true to
                    put on the second line, so it is left off. */}
                <div>{stage.stageName}</div>
                {stage.cmoStageID ? (
                  <div className={classes.stageSub}>{`Synthetic Step ${stage.cmoStageID}`}</div>
                ) : null}
              </TableCell>
              <Count classes={classes} last={last} text={formatCount(stage.plannedBatches)} />
              <Count classes={classes} last={last} text={formatCount(stage.completedBatches)} />
              <Count classes={classes} last={last} text={formatCount(stage.ongoingBatches)} />
              {/* FSD v3 §6: "Rounded percentage Completed / Planned, shown with
                  a progress bar beneath the value (green fill in proportion to
                  the percentage)". A stage with no batch plan shows NA over an
                  empty bar rather than a 0% fill, which would read as "nothing
                  done" instead of "nothing known". */}
              <TableCell
                className={[classes.bodyCell, classes.completionCell,
                  last ? classes.lastRowCell : '',
                  stage.completionPercent == null ? classes.dashCell : ''].filter(Boolean).join(' ')}
              >
                <div>{formatPercent(stage.completionPercent)}</div>
                <div className={classes.progressTrack}>
                  <span
                    className={classes.progressFill}
                    style={{ width: `${Math.max(0, Math.min(100, Number(stage.completionPercent) || 0))}%` }}
                  />
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

// FSD v3 §5: "CMO, Site, Project ID, CMP Product Code and Active Campaign ID".
const META_FIELDS = [
  ['CMO', 'cmoName'],
  ['Site', 'siteMfgPlant'],
  ['Project ID', 'projectId'],
  ['CMP Product Code', 'cmoProductCode'],
  ['Active Campaign ID', 'campaignId'],
];

// Each campaign is its own chevron card, keyed by its Campaign ID — never
// merged with another and never suppressed, even when a project has more than
// one campaign in the same section (FSD §5).
const CampaignBlock = ({ classes, block, expanded, onToggle }) => {
  const completed = block.campaignStatus === 'Completed';
  // Stage rows of nothing but dashes look like a failed load. They are not —
  // the campaign's batches simply have not landed in the extract yet — so say
  // so rather than leaving the reader to guess.
  const awaitingBatches =
    block.stages.length > 0 && block.stages.every((s) => !s.plannedBatches);

  return (
    <div className={classes.campaignCard}>
      <button
        type="button"
        className={classes.campaignChevron}
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} campaign ${block.campaignId}`}
      >
        {expanded ? <ExpandMoreIcon /> : <KeyboardArrowRightIcon />}
      </button>
      <div className={classes.campaignBody}>
        <div className={classes.campaignMeta}>
          {META_FIELDS.map(([label, key]) => (
            <div key={key}>
              <div className={classes.metaLabel}>{label}</div>
              <div className={classes.metaValue}>{block[key] || COUNT_DASH}</div>
            </div>
          ))}
          <div>
            <div className={classes.metaLabel}>Campaign Status</div>
            <span
              className={`${classes.statusPill} ${
                completed ? classes.statusCompleted : classes.statusOngoing
              }`}
            >
              {block.campaignStatus}
            </span>
          </div>
        </div>

        {expanded && block.stages.length > 0 && (
          <StageTable classes={classes} stages={block.stages} />
        )}
        {expanded && block.stages.length === 0 && (
          <div className={classes.cardNote}>No stages recorded for this campaign.</div>
        )}
        {expanded && awaitingBatches && (
          <div className={classes.cardNote}>
            No batches have been recorded against these stages yet.
          </div>
        )}
      </div>
    </div>
  );
};

const ExecutiveSummary = ({ onBack }) => {
  const classes = useStyles();

  // ── filters ───────────────────────────────────────────────────────────────
  const [cmoOptions, setCmoOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  // One value each (FSD v3 §4), '' meaning nothing chosen yet. ALL is a real
  // selection, not the absence of one, which is what makes Search available.
  const [cmo, setCmo] = useState('');
  const [site, setSite] = useState('');
  const [project, setProject] = useState('');

  // ── results ───────────────────────────────────────────────────────────────
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);

  // Ongoing open, Past closed — the state slides 6 and 7 show on arrival.
  const [showOngoing, setShowOngoing] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const [collapsedCampaigns, setCollapsedCampaigns] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchExecCmoOptionsAPI();
      if (!cancelled) setCmoOptions(res.data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // The three dropdowns cascade, so a change clears the selections below it —
  // in the handler, not the option-loading effect, so React batches it and a CMO
  // change reloads the project options once rather than twice.
  //
  // The option-loading effects key off these, so they must change identity only
  // when the REQUEST changes: `idsFor` maps both "nothing checked" and "All" to
  // an empty list, and a fresh [] each render would refetch the same thing on
  // every keystroke of the selection. Serialising pins the identity to the value.
  const cmoKey = JSON.stringify(idsFor(cmo));
  const siteKey = JSON.stringify(idsFor(site));
  const cmoIds = useMemo(() => JSON.parse(cmoKey), [cmoKey]);
  const siteIds = useMemo(() => JSON.parse(siteKey), [siteKey]);

  const handleCmoChange = useCallback((next) => {
    setCmo(next);
    setSite('');
    setProject('');
  }, []);

  const handleSiteChange = useCallback((next) => {
    setSite(next);
    setProject('');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchExecSiteOptionsAPI(cmoIds);
      if (!cancelled) setSiteOptions(res.data || []);
    })();
    return () => { cancelled = true; };
  }, [cmoIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchExecProjectOptionsAPI({ cdmoOrgIDs: cmoIds, cmoSites: siteIds });
      if (!cancelled) setProjectOptions(res.data || []);
    })();
    return () => { cancelled = true; };
  }, [cmoIds, siteIds]);

  // FSD §4 leaves the enable condition open ("Unconfirmed / Pending"); the
  // slides settle it — greyed while any dropdown is still on its placeholder
  // (slide 5, CMO chosen but not Site/Project), enabled once all three carry a
  // selection (slides 6–8).
  // FSD v3 §4: "Search is enabled only once all three filters - CMO, Site and
  // Project ID - have a value selected". ALL counts as a value.
  const canSearch = Boolean(cmo) && Boolean(site) && Boolean(project);
  const hasResults = results.ongoing.length > 0 || results.past.length > 0;

  // Search snapshots the filters it ran with. Refresh replays THAT snapshot, so
  // it re-syncs the results on screen instead of quietly turning into a new
  // search when the user has since touched a dropdown — and the report header
  // names the filters the exported numbers actually came from.
  const [applied, setApplied] = useState(null);

  const load = useCallback(async (selection) => {
    setLoading(true);
    const res = await fetchExecutiveSummaryAPI({
      cdmoOrgIDs: idsFor(selection.cmo),
      cmoSites: idsFor(selection.site),
      collabrixProjectIDs: idsFor(selection.project),
    });
    setResults(res.data);
    setApplied(selection);
    setLoading(false);
  }, []);

  const runSearch = useCallback(() => load({ cmo, site, project }), [load, cmo, site, project]);
  const refresh = useCallback(() => { if (applied) load(applied); }, [load, applied]);

  const toggleCampaign = useCallback((key) => {
    setCollapsedCampaigns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const labelsFor = (selection, options) => {
    if (!selection || selection === ALL) return 'All';
    const byId = new Map(options.map((o) => [String(o.id), o.label]));
    return byId.get(String(selection)) || selection;
  };

  const handleGenerateReport = () => {
    if (!applied) return;
    downloadExecutiveSummaryReport(results, {
      cmo: labelsFor(applied.cmo, cmoOptions),
      site: labelsFor(applied.site, siteOptions),
      project: labelsFor(applied.project, projectOptions),
    });
  };

  // FSD v3 §5: "an expand/collapse caret, a colored status dot (orange for
  // Ongoing Campaigns, green for Past Campaigns) and a badge with the number of
  // campaigns in that section, zero-padded to two digits".
  const renderSection = (title, dot, blocks, open, setOpen) => (
    <div className={classes.section}>
      <button type="button" className={classes.sectionHeader} onClick={() => setOpen(!open)}>
        {open ? <ExpandMoreIcon /> : <KeyboardArrowRightIcon />}
        <span className={classes.sectionDot} style={{ background: dot }} />
        <span className={classes.sectionTitle}>{title}</span>
        <span className={classes.sectionBadge}>{String(blocks.length).padStart(2, '0')}</span>
      </button>
      {open &&
        (blocks.length ? (
          <div className={classes.sectionBody}>
            {blocks.map((block) => {
              const key = String(block.collabrixCampaignID);
              return (
                <CampaignBlock
                  key={key}
                  classes={classes}
                  block={block}
                  expanded={!collapsedCampaigns.has(key)}
                  onToggle={() => toggleCampaign(key)}
                />
              );
            })}
          </div>
        ) : (
          <div className={classes.emptyPane}>
            <Typography className={classes.emptyText}>
              No campaigns in this section for the selected filters.
            </Typography>
          </div>
        ))}
    </div>
  );

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.backButton} onClick={onBack}>
            <ChevronLeftIcon />
            <Typography variant="body1">Back</Typography>
          </div>
          <Typography className={classes.title}>Executive Summary</Typography>
        </div>
      </div>

      <div className={classes.filterRow}>
        <SingleSelect
          classes={classes}
          label="Choose CMO:"
          placeholder="Select CMO..."
          options={cmoOptions}
          value={cmo}
          onChange={handleCmoChange}
        />
        <SingleSelect
          classes={classes}
          label="Choose Site:"
          placeholder="Select Site..."
          options={siteOptions}
          value={site}
          onChange={handleSiteChange}
        />
        <SingleSelect
          classes={classes}
          label="Choose Project ID:"
          placeholder="Select Project ID..."
          options={projectOptions}
          value={project}
          onChange={setProject}
        />
        <div className={classes.filterActions}>
          <Button
            variant="contained"
            className={classes.searchButton}
            disabled={!canSearch || loading}
            onClick={runSearch}
          >
            Search
          </Button>
          {/* Generate Report exports the results ON SCREEN, so it follows those
              rather than the dropdowns: editing a filter without re-running
              Search must not strand the user with a report they can still see
              but no longer download. The header names `applied`, so the sheet
              can never claim filters the numbers did not come from. */}
          <Button
            variant="contained"
            className={classes.reportButton}
            disabled={loading || !applied || !hasResults}
            onClick={handleGenerateReport}
          >
            Generate Report
          </Button>
        </div>
      </div>

      {/* FSD v3 §4: before the first Search the results area carries an
          empty-state message rather than nothing at all. The wording is the
          FSD's own. */}
      {!loading && !applied && (
        <div className={classes.emptyPane}>
          <Typography className={classes.emptyText}>
            Select your CMO, Site and Project ID to view an executive summary of all
            the planned, completed and ongoing batches per campaign.
          </Typography>
        </div>
      )}

      {loading && (
        <div className={classes.centered}>
          <CircularProgress size={28} />
        </div>
      )}

      {!loading && applied && (
        <>
          {/* One Last Synced / Refresh per screen (FSD §5). Refresh re-runs the
              same query, so both sections update together. */}
          <div className={classes.syncRow}>
            <span className={classes.syncText}>
              {results.lastSyncedAt
                ? `Last Synced @ ${moment(results.lastSyncedAt).format('DD-MMM-YYYY HH:mm:ss').toUpperCase()}`
                : 'Last Synced @ —'}
            </span>
            <Button
              className={classes.refreshButton}
              endIcon={<RefreshIcon fontSize="small" />}
              onClick={refresh}
            >
              Refresh
            </Button>
          </div>

          {/* FSD v3 §5: four tiles between Last Synced and the sections.
              Planned campaigns appear ONLY here - they are deliberately absent
              from both sections, so this tile is the only place a campaign that
              has not started is visible at all. */}
          <div className={classes.tileRow}>
            {TILES.map(([key, label, accent]) => (
              <SummaryTile
                key={key}
                classes={classes}
                accent={accent}
                count={results.tiles ? results.tiles[key] : null}
                label={label}
              />
            ))}
          </div>

          <div className={classes.tipLine}>
            Tip &mdash; click View in the Action column to see a stage&rsquo;s batches.
          </div>

          {hasResults ? (
            /* FSD §5: "the results pane scrolls vertically when content exceeds
               the visible area". Scoping the scroll to this pane keeps the
               filter bar and Last Synced in place while the campaign list moves
               under them, rather than scrolling the whole screen away. */
            <div className={classes.resultsPane}>
              {renderSection('Ongoing Campaigns', '#E8833A', results.ongoing, showOngoing, setShowOngoing)}
              {renderSection('Past Campaigns', '#2E9E4F', results.past, showPast, setShowPast)}
            </div>
          ) : (
            <div className={classes.emptyPane}>
              <Typography className={classes.emptyText}>
                No campaigns match the selected CMO / Site / Project ID.
              </Typography>
            </div>
          )}
        </>
      )}
    </div>
  );
};

ExecutiveSummary.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default ExecutiveSummary;
